
import React, { useEffect, useState } from 'react';
import { Zap, Plus, Settings, Bike, RefreshCw, Bell, Leaf, LogOut, BarChart3, Bot, Sun, ChevronDown, ChevronUp } from 'lucide-react';
import { NotificationCenter } from '../NotificationCenter';
import { SystemConfig, UserRole, Order } from '../../types';
import { BaseTooltip } from '../shared/BaseTooltip';

interface Props {
  isSimulating: boolean;
  isSimpleView: boolean;
  isSyncing?: boolean;
  onOpenDeliveryModal: () => void;
  onSimulateWebhook: () => void;
  onManualSync?: () => void;
  onNewOrderModal: () => void;
  onOpenSettings: () => void;
  onOpenStaffPin: () => void;
  onLogout: () => void;
  onToggleSimpleView: () => void;
  autoPilotEnabled: boolean;
  onToggleAutoPilot: () => void;
  config: SystemConfig;
  orders: Order[];
  onToggleEconomicMode: () => void;
  onToggleSolarMode: () => void;
  userRole?: UserRole;
  viewMode: 'triage' | 'map' | 'analytics';
  setViewMode: (mode: 'triage' | 'map' | 'analytics') => void;
}

export const NavigationHub: React.FC<Props> = ({
  isSimulating, isSimpleView, isSyncing = false, onManualSync, onNewOrderModal, onOpenSettings, onOpenStaffPin, onLogout,
  autoPilotEnabled, onToggleAutoPilot, config, orders, onToggleEconomicMode, onToggleSolarMode, userRole, viewMode, setViewMode,
  onOpenDeliveryModal, onSimulateWebhook, onToggleSimpleView
}) => {
  const [isMobile, setIsMobile] = useState(false);
  /* v47.0: Auto-Hide Navigation (macOS Style) */
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const isAdmin = userRole === 'admin';

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(max-width: 1023px)');

    const applyViewportMode = (matches: boolean) => {
      setIsMobile(matches);
      if (matches) {
        setIsCollapsed(false);
      }
    };

    applyViewportMode(mediaQuery.matches);

    const handleChange = (event: MediaQueryListEvent) => {
      applyViewportMode(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // v37.1: Acesso Seguro para evitar crash por campos indefinidos
  const hasNotifications = React.useMemo(() => {
    const oracle = config?.oracle;
    if (!oracle?.active) return false;
    if (!oracle.calibratedAt) return true;
    return (Date.now() - oracle.calibratedAt) / (1000 * 60 * 60 * 24) > 60;
  }, [config?.oracle]);

  return (
    <>
      {/* Trigger Zone (Top of Screen) */}
      {!isMobile && (
        <button
          type="button"
          className="fixed top-0 left-0 w-full h-3 z-[100] hover:bg-sal-cyber-teal/10 transition-colors"
          onMouseEnter={() => setIsCollapsed(false)}
          aria-label="Expandir navegação"
        />
      )}

      {/* Main Navigation Container */}
      <nav
        className={`bg-[var(--card-bg)] text-[var(--text-main)] shrink-0 shadow-[var(--shadow-md)] z-50 border-white/5 relative transition-all duration-300 ease-in-out overflow-hidden ${isCollapsed ? 'h-0 border-b-0 opacity-0' : 'h-[64px] xl:h-[68px] 2xl:h-[72px] border-b opacity-100'}`}
        onMouseLeave={isMobile ? undefined : () => setIsCollapsed(true)}
        aria-label="Navegação principal"
      >
        <div className="px-3 xl:px-4 py-2 xl:py-3 flex justify-between items-center h-full">
          <div className="flex items-center gap-2 xl:gap-3">
            <div className="bg-sal-cyber-teal p-1.5 rounded-lg text-badge-text-inv font-bold shadow-[0_0_15px_rgba(6,182,212,0.3)] border-none ring-0">
              <Zap className="w-5 h-5 fill-current" />
            </div>
            <div>
              <h1 className="font-bold text-xl leading-none tracking-tight uppercase-ops text-text-primary">S.A.L. <span className="text-sal-cyber-teal">Rota</span></h1>
              <p className="text-[10px] text-text-muted font-black uppercase-ops tracking-widest">{isAdmin ? 'CONSOLE DE ADMIN' : 'OPERACIONAL'}</p>
            </div>

            {isAdmin && (
              <BaseTooltip data={{ title: "Painel Motoboys", description: "Visualizacao e gestao rapida da equipe.", type: 'time' }}>
                <button
                  type="button"
                  onClick={onOpenStaffPin}
                  className="md:hidden p-2.5 bg-status-info/10 hover:bg-status-info/20 rounded-xl text-status-info transition-all shrink-0"
                  aria-label="Abrir painel de motoboys"
                >
                  <Bike className="w-5 h-5" />
                </button>
              </BaseTooltip>
            )}
          </div>

          <div className="flex items-center gap-1 xl:gap-2 min-w-0 overflow-x-auto whitespace-nowrap [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden [&>*]:shrink-0">
            {isAdmin && (
              <button
                type="button"
                onClick={() => setViewMode(viewMode === 'analytics' ? 'triage' : 'analytics')}
                className={`flex items-center gap-1.5 xl:gap-2 px-2.5 xl:px-4 py-1.5 xl:py-2 rounded-xl text-xs font-black uppercase-ops transition-all border-2 mr-1 ${viewMode === 'analytics' ? 'bg-sal-cyber-teal text-badge-text-inv border-sal-cyber-teal shadow-[var(--shadow-lg)]' : 'bg-transparent border-border-dim text-text-muted hover:bg-app-bg'}`}
                aria-label={viewMode === 'analytics' ? 'Voltar para operação' : 'Ir para analytics'}
              >
                <BarChart3 className="w-4 h-4" />
                <span className="hidden lg:inline">{viewMode === 'analytics' ? 'OPERAÇÃO' : 'ANÁLISE / DADOS'}</span>
                <span className="lg:hidden">{viewMode === 'analytics' ? 'OP' : 'DADOS'}</span>
              </button>
            )}

            {isAdmin && (
              <BaseTooltip data={{ title: "Guarda de Orçamento", description: "Ative para economizar API do Google.", formula: "PREJUÍZOS: Estimativas simplificadas.", type: 'alert' }}>
                <button
                  type="button"
                  onClick={onToggleEconomicMode}
                  className={`p-2 rounded-lg border transition-all ${config?.isEconomicMode ? 'bg-status-ready/10 border-status-ready/30 text-status-ready' : 'bg-transparent border-border-dim text-icon-muted hover:bg-app-bg'}`}
                  aria-label={config?.isEconomicMode ? 'Desativar Budget Guard' : 'Ativar Budget Guard'}
                >
                  <Leaf className="w-4 h-4" />
                </button>
              </BaseTooltip>
            )}

            <BaseTooltip data={{ title: "Modo Solar", description: "Cores de alto contraste para leitura sob luz natural.", formula: "IDEAL PARA: Uso externo ou telas reflexivas.", type: 'logic' }}>
              <button
                type="button"
                onClick={onToggleSolarMode}
                className={`p-2 rounded-lg border transition-all ${config?.isSolarMode ? 'bg-status-warning/10 border-status-warning/30 text-status-warning shadow-sm' : 'bg-transparent border-border-dim text-icon-muted hover:bg-app-bg'}`}
                aria-label={config?.isSolarMode ? 'Desativar modo solar' : 'Ativar modo solar'}
              >
                <Sun className="w-4 h-4" />
              </button>
            </BaseTooltip>

            <div className="w-px h-6 bg-border-dim mx-1"></div>

            <div className="relative">
              <button
                type="button"
                onClick={() => setIsNotifOpen(!isNotifOpen)}
                className={`p-2 rounded-lg transition-all ${isNotifOpen ? 'bg-sal-cyber-teal/10 text-sal-cyber-teal shadow-sm' : 'text-icon-default hover:text-text-primary hover:bg-app-bg'}`}
                aria-label="Centro de notificações"
              >
                <Bell className="w-5 h-5" />
                {hasNotifications && <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-status-critical rounded-full border-2 border-card-bg animate-pulse" />}
              </button>
              <NotificationCenter isOpen={isNotifOpen} onClose={() => setIsNotifOpen(false)} config={config} orders={orders} onOpenSettings={onOpenSettings} />
            </div>

            <div className="w-px h-6 bg-border-dim mx-1"></div>

            <BaseTooltip data={{ title: "Sincronizar", description: "Atualiza os pedidos e status do sistema manualmente.", type: 'time' }}>
              <button
                type="button"
                onClick={onManualSync}
                disabled={isSyncing}
                className={`p-2 rounded-lg border transition-all ${isSyncing ? 'bg-sal-cyber-teal/20 border-sal-cyber-teal text-sal-cyber-teal' : 'bg-transparent border-border-dim text-icon-default hover:bg-app-bg'}`}
                aria-label="Sincronizar pedidos manualmente"
              >
                <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
              </button>
            </BaseTooltip>

            {isAdmin && (
              <BaseTooltip data={{ title: "Piloto Automático", description: "O sistema decide e despacha pedidos automaticamente.", type: 'financial' }}>
                <button
                  type="button"
                  onClick={onToggleAutoPilot}
                  className={`p-2 rounded-lg border transition-all ${config?.autoPilotEnabled ? 'bg-status-info/10 border-status-info/30 text-status-info shadow-sm' : 'bg-transparent border-border-dim text-icon-default hover:bg-app-bg'}`}
                  aria-label={config?.autoPilotEnabled ? 'Desativar piloto automático' : 'Ativar piloto automático'}
                >
                  <Bot className="w-4 h-4" />
                </button>
              </BaseTooltip>
            )}

            <button
              type="button"
              onClick={onNewOrderModal}
              className="bg-sal-cyber-teal text-white hover:brightness-110 px-3 xl:px-4 py-1.5 xl:py-2 rounded-xl text-xs xl:text-sm font-black flex items-center gap-1.5 xl:gap-2 shadow-lg uppercase-ops transition-all active:scale-95 ml-1 xl:ml-2"
              aria-label="Criar novo pedido"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Novo</span>
              <span className="sm:hidden">+</span>
            </button>

            <div className="w-px h-6 bg-border-dim mx-2"></div>

            {isAdmin && (
              <>
                <BaseTooltip data={{ title: "Configurações", description: "Ajustes de sistema, frota e integrações.", type: 'logic' }}>
                  <button
                    type="button"
                    onClick={onOpenSettings}
                    className="p-2.5 bg-border-dim/10 hover:bg-border-dim/20 rounded-xl text-icon-default hover:text-text-primary transition-all"
                    aria-label="Abrir configurações do sistema"
                  >
                    <Settings className="w-5 h-5" />
                  </button>
                </BaseTooltip>
                <BaseTooltip data={{ title: "Painel Motoboys", description: "Visualização e gestão rápida da equipe.", type: 'time' }}>
                  <button
                    type="button"
                    onClick={onOpenStaffPin}
                    className="p-2.5 bg-status-info/10 hover:bg-status-info/20 rounded-xl text-status-info transition-all"
                    aria-label="Abrir painel de motoboys"
                  >
                    <Bike className="w-5 h-5" />
                  </button>
                </BaseTooltip>
              </>
            )}

            <BaseTooltip data={{ title: "Sair", description: "Encerrar sessão segura.", type: 'alert' }}>
              <button
                type="button"
                onClick={onLogout}
                className="p-2.5 bg-status-critical/10 hover:bg-status-critical/20 rounded-xl text-status-critical transition-all active:scale-90 ml-1"
                aria-label="Encerrar sessão"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </BaseTooltip>
          </div>
        </div>

        {/* Manual Close Push Tab */}
        {!isMobile && (
          <div
            className="absolute bottom-0 left-1/2 -translate-x-1/2 z-[60] flex flex-col items-center"
          >
            <button
              type="button"
              className="bg-card-bg/80 backdrop-blur-sm border border-b-0 border-white/10 rounded-t-xl px-4 py-0.5 hover:bg-sal-cyber-teal/10 shadow-sm group transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setIsCollapsed(true);
              }}
              aria-label="Recolher menu de navegação"
            >
              <ChevronUp className="w-4 h-4 text-icon-muted group-hover:text-sal-cyber-teal transition-colors opacity-70" />
            </button>
          </div>
        )}
      </nav>

      {/* Manual Open Pull Tab (Visible when collapsed) */}
      {!isMobile && (
        <div
          className={`fixed top-0 left-1/2 -translate-x-1/2 z-[90] transition-all duration-300 ${isCollapsed ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}
        >
          <button
            type="button"
            className="bg-card-bg/80 backdrop-blur-sm border border-t-0 border-white/10 rounded-b-xl px-4 py-0.5 hover:bg-sal-cyber-teal/10 shadow-sm group transition-colors"
            onClick={() => setIsCollapsed(false)}
            aria-label="Expandir menu de navegação"
          >
            <ChevronDown className="w-4 h-4 text-icon-muted group-hover:text-sal-cyber-teal transition-colors opacity-70" />
          </button>
        </div>
      )}
    </>
  );
};
